<template>
  <WdsPage class="client-shell" :class="{ 'client-shell--overlay': overlay }">
    <WdsStack direction-column gap-sm class="tw-w-full" :class="{ 'tw-pb-8': overlay }">
      <WdsStack
        direction-row
        justify-space-between
        class="tw-w-full"
        data-test="client-page-breadcrumbs"
      >
        <WdsStack justify-center align-center gap-sm>
          <WdsButtonGhost
            v-if="overlay"
            secondary
            data-test="client-page-back"
            @click="emit('close')"
          >
            <WdsStack align-center gap-sm>
              <WdsIcon name="arrow-left" :size="16" />
              {{ $t('Clients') }}
            </WdsStack>
          </WdsButtonGhost>
          <WdsButtonGhost v-else secondary :href="clientsIndexUrl" data-test="client-page-back">
            <WdsStack align-center gap-sm>
              <WdsIcon name="arrow-left" :size="16" />
              {{ $t('Clients') }}
            </WdsStack>
          </WdsButtonGhost>
        </WdsStack>
      </WdsStack>

      <ContentLoadingErrorNotice
        v-if="gone"
        class="tw-mt-8"
        :title="$t('This client is no longer available')"
        :description="$t('The link to this client was removed, or you no longer have access to it.')"
        :button-text="$t('Back to clients')"
        :on-button-click="goBack"
        hide-status-button
        data-test="client-page-gone"
      />
      <template v-else>
        <PageHeader class="client-header">
          <template #header-title>
            <ClientChip
              :client-relation-id="clientRelationId"
              lg
              static
              data-test="client-page-title"
            />
          </template>
        </PageHeader>

        <div class="tw-mt-4">
          <WdsTabs :active-tab-key="tab" @update:active-tab-key="selectTab">
            <WdsTab :tab-key="ClientPageTab.Overview" :title="$t('Overview')">
              <ClientOverview
                :client-relation-id="clientRelationId"
                @view-people="
                  navigate({
                    tab: ClientPageTab.People,
                    people: agencyLink ? PeopleSegment.Agencies : PeopleSegment.Client,
                  })
                "
              />
            </WdsTab>
            <WdsTab
              v-if="canManageAgencies"
              :tab-key="ClientPageTab.StaffingAgencies"
              :title="$t('Staffing agencies')"
            >
              <template #title>
                <WdsStack align-center gap-sm>
                  {{ $t('Staffing agencies') }}
                  <WdsLabel v-if="linkedCount > 0" xs data-test="client-agencies-count">
                    {{ linkedCount }}
                  </WdsLabel>
                </WdsStack>
              </template>
              <WdsStack justify-end class="tw-mb-4">
                <WdsButton
                  primary
                  :disabled="!clientCompany"
                  data-test="client-page-link-agencies"
                  @click="linking = true"
                >
                  {{ linkedCount > 0 ? $t('Manage staffing agencies') : $t('Link staffing agencies') }}
                </WdsButton>
              </WdsStack>
              <KeepAlive>
                <ClientRecruitersTable
                  v-if="clientCompany && tab === ClientPageTab.StaffingAgencies"
                  v-model:linking="linking"
                  :client-company-id="clientCompany.id"
                  :client-name="clientCompany.name"
                />
              </KeepAlive>
              <LoadingList
                v-if="!clientCompany && tab === ClientPageTab.StaffingAgencies"
                :count="3"
                no-bg
                data-test="client-agencies-loading"
              />
            </WdsTab>
            <WdsTab :tab-key="ClientPageTab.People" :title="$t('People')">
              <template #title>
                <WdsStack align-center gap-sm>
                  {{ $t('People') }}
                  <WdsLabel v-if="peopleCount" xs data-test="client-people-count">
                    {{ peopleCount }}
                  </WdsLabel>
                </WdsStack>
              </template>
              <ClientPeopleTab
                v-if="tab === ClientPageTab.People"
                :segment="peopleSegment"
                :client-relation-id="clientRelationId"
                :client-name="(clientCompany ?? agencyLink?.client)?.name ?? null"
                :can-manage-owners="(header ?? agencyLink)?.viewerCanManageOwners ?? false"
                :can-manage-agencies="canManageAgencies"
                :is-agency-link="!!agencyLink"
                :is-direct="agencyLink?.isDirect ?? null"
                @update:segment="selectSegment"
                @open-agencies="selectTab(ClientPageTab.StaffingAgencies)"
              />
            </WdsTab>
          </WdsTabs>
        </div>
      </template>
    </WdsStack>
  </WdsPage>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { useRoute, useRouter } from 'vue-router'
import {
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsLabel,
  WdsPage,
  WdsStack,
  WdsTab,
  WdsTabs,
} from '@worksome/wds-components'
import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ClientChip from '@/components/Clients/ClientChip.vue'
import PageHeader from '@/components/PageHeader.vue'
import ClientOverview from '@/components/Clients/ClientOverview.vue'
import ClientRecruitersTable from '@/components/Clients/ClientRecruitersTable.vue'
import ClientPeopleTab from '@/components/Clients/People/ClientPeopleTab.vue'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'
import { ClientPageTab, PeopleSegment } from '@/components/Clients/clientPageTabs'
import { ClientPeopleCountsQuery } from '@/components/Clients/graphql/clientPeopleCountsQuery'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

const ClientPageHeaderFields = graphql(`
  fragment ClientPageHeader on CompanySupplier {
    id
    viewerCanManageStaffingAgencies
    viewerCanManageOwners
    company {
      id
      name
    }
    ...ClientChip
  }
`)

const GET_CLIENT_PAGE = graphql(`
  query SupplierClientPage($id: ID!) {
    clientRelation(id: $id) {
      id
      ... on SupplierClient {
        isDirect
        viewerCanManageOwners
        client {
          id
          name
        }
      }
      ...ClientChip
      ...ClientPageHeader
    }
  }
`)

const AGENCY_COUNTS = graphql(`
  query SupplierClientAgencyCounts($endClients: [ID!]) {
    linked: companyRecruiters(first: 1, endClients: $endClients) {
      paginatorInfo {
        total
      }
    }
  }
`)

const props = defineProps<{ clientRelationId: string; overlay?: boolean }>()

const emit = defineEmits<{ (e: 'close'): void }>()

const { result: pageResult } = useQuery(
  GET_CLIENT_PAGE,
  () => ({ id: props.clientRelationId }),
  { clientId: 'publicApi' },
)

const header = computed(() =>
  pageResult.value?.clientRelation?.__typename === 'CompanySupplier'
    ? (useFragment(ClientPageHeaderFields, pageResult.value.clientRelation) ?? null)
    : null,
)
const agencyLink = computed(() =>
  pageResult.value?.clientRelation?.__typename === 'SupplierClient'
    ? pageResult.value.clientRelation
    : null,
)
const clientCompany = computed(() => header.value?.company ?? null)
// Unknown until the header lands, so the tab's skeleton shows; agency links never get it.
const canManageAgencies = computed(() =>
  header.value ? header.value.viewerCanManageStaffingAgencies : pageResult.value === undefined,
)
// Null means the API answered without the relation; undefined only means it has not answered yet.
const gone = computed(
  () => pageResult.value !== undefined && !header.value && !agencyLink.value,
)

const route = useRoute()
const router = useRouter()

// Reads the address bar, not the route: the tables' encoders write there without the router.
const navigate = (query: Record<string, string | undefined>) =>
  router.push({
    path: window.location.pathname,
    query: { ...Object.fromEntries(new URLSearchParams(window.location.search)), ...query },
  })

const tab = computed(() => {
  const requested = (route.query.tab as string | undefined) ?? ClientPageTab.Overview

  return requested === ClientPageTab.StaffingAgencies && !canManageAgencies.value
    ? ClientPageTab.Overview
    : requested
})

const peopleSegment = computed(() => route.query.people as string | undefined)

const selectTab = (key: string) => navigate({ tab: key === ClientPageTab.Overview ? undefined : key })

const selectSegment = (segment?: string) =>
  navigate({ people: segment === PeopleSegment.Team ? undefined : segment })

const { result: counts } = useQuery(
  AGENCY_COUNTS,
  () => ({ endClients: [clientCompany.value?.id ?? ''] }),
  () => ({ clientId: 'publicApi', enabled: !!clientCompany.value && canManageAgencies.value }),
)

const linkedCount = computed(() => counts.value?.linked.paginatorInfo.total ?? 0)

// Same variables as the People tab, so both read one request.
const { result: peopleCounts } = useQuery(
  ClientPeopleCountsQuery,
  () => ({
    client: props.clientRelationId,
    accounts: [currentAccount.globalId],
    companyRelation: !agencyLink.value,
  }),
  () => ({ clientId: 'publicApi', enabled: !!header.value || !!agencyLink.value }),
)

const peopleCount = computed(
  () =>
    (peopleCounts.value?.team?.paginatorInfo.total ?? 0) +
    (peopleCounts.value?.recruiters?.paginatorInfo.total ?? 0) +
    (peopleCounts.value?.clientTeam?.paginatorInfo.total ?? 0),
)

const linking = ref(false)

const clientsIndexUrl = computed(() => laravelNamedRoute(namedRoutes.frontend.clients.index, {}))

function goBack() {
  if (props.overlay) {
    emit('close')
    return
  }

  window.location.assign(clientsIndexUrl.value)
}
</script>

<style lang="scss" scoped>
.client-header {
  @apply tw-mb-0;
  @apply tw-mt-2;
}

.client-shell:not(.client-shell--overlay) {
  height: auto;
  min-height: 0;
  max-height: none;
  overflow: visible;

  :deep(.wds-right) {
    height: auto;
    min-height: 0;
    overflow-y: visible;
  }
}

.client-shell :deep(.wds-right) {
  padding-top: 2rem;
}

.client-shell :deep(.wds-right-slot) {
  width: 100%;
}

.client-shell--overlay :deep(.wds-right-slot) {
  max-width: 1170px;
  padding: 0 15px;
}
</style>
